/**
 * AssistantUiThread.tsx — assistant-ui + AG-UI chat runtime for the
 * teacher toolkit workspace. The shell around it decides subject,
 * course-assistance mode, private note context, and contribution flow.
 */
import { useMemo, useRef, useEffect, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import {
  AssistantRuntimeProvider,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useComposer,
  useThread,
} from "@assistant-ui/react";
import {
  getProgressSnapshot,
  subscribeProgress,
} from "./progressStore";
import { AttachmentAddButton, AttachmentChipsRow } from "./attachments/AttachmentStrip";
import { TeacherMicButton } from "./MicButton";
import { TeacherAttachmentChip } from "./attachments/TeacherAttachmentChip";
import { ArtifactAutoSave } from "./attachments/ArtifactAutoSave";
// PlanPickerButton intentionally not mounted — the composer felt
// cluttered with an extra pill next to the paperclip. The component
// file stays in tree so we can re-enable it if we change our minds.
// Auto-attach by "week N" phrasing still runs silently via the
// watcher, which is the load-bearing part of the integration.
import { PlanAutoAttachWatcher } from "../teachingPlans/PlanAutoAttachWatcher";
import { useAgUiRuntime } from "@assistant-ui/react-ag-ui";
import type { ThreadHistoryAdapter } from "@assistant-ui/core";
import { HttpAgentWithContext } from "./attachments/HttpAgentWithContext";
import { createConversationHistoryAdapter } from "./historyAdapter";
import { MarkdownMessageText } from "./MarkdownText";
import type { AttachmentCapabilities } from "./attachments/types";
import { makeConversationAttachmentAdapter } from "./attachments/ConversationAttachmentAdapter";
import "../workbench/workbench.css";

export function AssistantUiThread({
  subject,
  subjects: _subjects,
  subjectIdx: _subjectIdx,
  onSubjectChange: _onSubjectChange,
  teacherId,
  authHeader,
  conversationId,
  loadConversationHistory = true,
  attachmentCapabilities,
  runContext,
  onFirstMessage,
}: {
  subject: { agentId: string; labelKey: string; label?: string; isFallback?: boolean };
  subjects: ReadonlyArray<{ agentId: string; labelKey: string }>;
  subjectIdx: number;
  onSubjectChange: (idx: number) => void;
  teacherId: string;
  authHeader: Record<string, string>;
  conversationId: string | null;
  loadConversationHistory?: boolean;
  attachmentCapabilities: AttachmentCapabilities;
  runContext?: Record<string, unknown>;
  /** Fires once when the first user message hits the runtime. Used by
   *  the teacher workbench (and any future caller) to gate UI surfaces
   *  on "the chat has actually started." Stable identity — called at
   *  most once per AssistantUiThread mount; remount (e.g., subject
   *  switch) resets. */
  onFirstMessage?: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();

  // Fresh HttpAgent on each (subject, conversation) tuple — assistant-ui
  // runtime is keyed on the agent identity; constructing a new agent
  // gives us the same "remount on switch" semantics as the CopilotKit
  // Provider's key= prop in the hand-rolled variant.
  const agent = useMemo(() => {
    // Dispatch scope must match registration scope: nova-os agent lookup
    // (GetForApp) is EXACT — no cross-scope fallback. The M3 subject
    // employees (school-employee-*) are registered under the school app
    // scope and need `?app=school`; kernel agents (nova-orchestrator
    // fallback, nova-* etc.) 404 WITH the param. `info?app=school` merges
    // both scopes but doesn't say which is which, so we go by the
    // school-employee- naming convention (employees.py generates it).
    const schoolScoped = subject.agentId.startsWith("school-employee-");
    const url = `/api/copilotkit/${subject.agentId}${schoolScoped ? "?app=school" : ""}`;
    return new HttpAgentWithContext({
      url,
      // threadId is the AG-UI-canonical conversation channel: it lands
      // in RunAgentInput.threadId and Nova OS uses *that*, not the
      // X-Conversation-Id header, to scope conversation persistence.
      // Without this, AbstractAgent auto-generates a fresh UUID per
      // HttpAgent instance — and since the agent useMemo can be
      // re-evaluated mid-session by upstream dep churn, every turn
      // gets a brand-new threadId → a brand-new conversation row on
      // the backend. The X-Conversation-Id header is kept as a hint
      // for any future Nova OS code path that wants to honour it,
      // but threadId is what actually pins the thread.
      ...(conversationId ? { threadId: conversationId } : {}),
      headers: {
        ...authHeader,
        "X-Platform-User": teacherId,
        "X-End-User": teacherId,
        ...(conversationId ? { "X-Conversation-Id": conversationId } : {}),
      },
      // Include the UI locale (EN/中 toggle) so the backend planner
      // localises its step descriptions to the language the teacher
      // selected — deterministic, not content-detected (an attached
      // document's text can otherwise pull detection off-language).
      forwardedProps: { ...runContext, locale: i18n.language },
    });
  }, [subject.agentId, subject.isFallback, authHeader, teacherId, conversationId, runContext, i18n.language]);

  // Wire conversation history so selecting a sidebar row pre-populates
  // the thread with the message log from /v1/conversations/<id>.
  // Nova OS owns write-back via the AG-UI streaming path, so `append`
  // is a no-op — we don't keep a client mirror.
  const history = useMemo<ThreadHistoryAdapter>(
    () => createConversationHistoryAdapter({
      authHeader,
      conversationId,
      loadHistory: loadConversationHistory,
    }),
    [conversationId, authHeader, loadConversationHistory],
  );

  // Build the attachment adapter only when the kernel advertises support
  // AND we have a conversation_id to scope uploads to. When attachments
  // are disabled, still loading (capabilities default to DISABLED until
  // GET /api/copilotkit/info resolves), OR no conversation is selected,
  // the adapter is undefined and the AttachmentStrip (paperclip) is not
  // rendered — see the {attachments && ...} guard below. We render the
  // strip on adapter readiness rather than relying on the library to
  // auto-hide ComposerPrimitive.AddAttachment: it does NOT reliably do
  // so, so an unconditional paperclip stayed clickable during the info-
  // fetch window and threw "Attachments are not supported" on add
  // (nova-os-school#35).
  const attachments = useMemo(() => {
    if (!attachmentCapabilities.enabled) return undefined;
    if (!conversationId) return undefined;
    return makeConversationAttachmentAdapter({
      conversationId,
      authHeader,
      capabilities: attachmentCapabilities,
    });
  }, [attachmentCapabilities, conversationId, authHeader]);

  const runtime = useAgUiRuntime({ agent, adapters: { history, attachments } });

  return (
    <div className="teacher-thread-main">
      {/* The chapter-head identity (subject h1 + agent code + subject
          switcher) was redundant with the workbench topbar's identity
          row + picker dropdowns (simplification spec D4 — "ONE row at
          the top of the workspace"). Intentionally removed; subject
          + mode selection happens via topbar pickers. _subjects,
          _subjectIdx, _onSubjectChange remain on the props so the
          API stays back-compat for any future chat surface that
          re-renders the switcher inline. */}

      <AssistantRuntimeProvider runtime={runtime}>
        {onFirstMessage && <FirstMessageDetector onFire={onFirstMessage} />}
        <ArtifactAutoSave />
        {/* Gate on adapter readiness for the same reason as AttachmentStrip
            (#35): this watcher calls composer.addAttachment on a debounce,
            which throws "Attachments are not supported" if the adapter isn't
            registered yet. Mounting it only once the adapter is ready avoids
            that; on mount its effect runs against the current composer text,
            so a "week N" typed during the info-fetch window still attaches. */}
        {attachments && <PlanAutoAttachWatcher />}
        <ThreadPrimitive.Root className="teacher-thread-root">
          <ThreadPrimitive.Viewport
            className="teacher-thread-viewport"
            data-reveal
            style={{ "--i": 2 } as React.CSSProperties}
          >
            <ThreadPrimitive.Empty>
              <div className="teacher-thread-empty">
                {t("assistantUi.emptyPrompt", { agent: subject.label ?? subject.agentId })}
              </div>
            </ThreadPrimitive.Empty>

            <ThreadPrimitive.Messages
              components={{
                UserMessage: () => <TeacherUserMessage />,
                AssistantMessage: () => (
                  <TeacherAssistantMessage agentId={subject.agentId} />
                ),
              }}
            />

            <RunningIndicator />
          </ThreadPrimitive.Viewport>

          <div className="teacher-input-region">
            <ComposerPrimitive.Root className="teacher-input-shell">
              {attachments && <AttachmentChipsRow />}
              <div className="teacher-input-row">
                {attachments && <AttachmentAddButton />}
                <ComposerPrimitive.Input
                  className="teacher-input"
                  rows={1}
                  autoFocus
                  placeholder={t("assistantUi.inputPlaceholder", { agent: subject.label ?? subject.agentId })}
                />
                <div className="teacher-input-underline" />
                <TeacherMicButton authHeader={authHeader} />
                <TeacherSendButton ariaLabel={t("assistantUi.send")} />
              </div>
            </ComposerPrimitive.Root>
            <div className="teacher-input-help">
              <span>
                <kbd>Enter</kbd> {t("assistantUi.send")}
                <span style={{ margin: "0 12px" }}>·</span>
                <kbd>Shift Enter</kbd> {t("assistantUi.newline")}
              </span>
            </div>
          </div>
        </ThreadPrimitive.Root>

        {/* AgentCard + LiveAgentActivity used to render here as a right rail
            inside the chat surface. The teacher workbench's RightRail
            (simplification spec D1) now owns the LiveAgentActivity render,
            and the topbar carries the agent identity (D4), so mounting
            AgentCard here would duplicate both. Intentionally removed —
            AgentCard.tsx stays in tree for /chat-style consumers (none
            today, but a future single-thread chat surface could reuse it). */}
      </AssistantRuntimeProvider>
    </div>
  );
}

function TeacherSendButton({
  ariaLabel,
}: {
  ariaLabel: string;
}): JSX.Element {
  // useComposer with a selector is the reactive path — it re-renders only
  // when the returned value changes. useComposerRuntime().getState() is
  // not reactive on its own, so we use this hook instead.
  const anyUploading = useComposer(
    (s) =>
      s.attachments.some(
        (a) => a.status.type === "running",
      ),
  );
  return (
    <ComposerPrimitive.Send
      className="teacher-send"
      aria-label={ariaLabel}
      disabled={anyUploading}
      title={anyUploading ? undefined : ariaLabel}
    >
      <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="square"
      >
        <path d="M2 8 L8 2 L14 8 M8 2 L8 14" />
      </svg>
    </ComposerPrimitive.Send>
  );
}

function TeacherUserMessage(): JSX.Element {
  const { t } = useTranslation();
  return (
    <MessagePrimitive.Root className="teacher-message from-user">
      <div className="role-bar" />
      <div className="body">
        <div className="header">
          <span className="role">{t("assistantUi.teacher")}</span>
        </div>
        <div className="content">
          {/* Show the attachments the teacher sent with this turn (name +
              thumb) so the sent message reflects what was uploaded, like
              other chat platforms. Message-source chips are immutable —
              TeacherAttachmentChip hides its remove button for them. */}
          <div className="teacher-message-attachments">
            <MessagePrimitive.Attachments
              components={{ Attachment: TeacherAttachmentChip }}
            />
          </div>
          <MessagePrimitive.Parts components={{ Text: MarkdownMessageText }} />
        </div>
      </div>
    </MessagePrimitive.Root>
  );
}

function FirstMessageDetector({
  onFire,
}: {
  onFire: () => void;
}): null {
  const thread = useThread();
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    const messages = thread.messages ?? [];
    const hasUserMessage = messages.some((m) => m.role === "user");
    if (hasUserMessage) {
      firedRef.current = true;
      onFire();
    }
  }, [thread.messages, onFire]);
  return null;
}

function TeacherAssistantMessage({
  agentId,
}: {
  agentId: string;
}): JSX.Element {
  return (
    <MessagePrimitive.Root className="teacher-message from-assistant">
      <div className="role-bar" />
      <div className="body">
        <div className="header">
          <span className="role">
            <span className="teacher-agent-dot" />
            {agentId}
          </span>
        </div>
        <div className="content">
          <MessagePrimitive.Parts components={{ Text: MarkdownMessageText }} />
        </div>
      </div>
    </MessagePrimitive.Root>
  );
}

// Inline "thinking" indicator rendered in the message stream while the agent
// plans / retrieves — the long window before any answer text streams. Mirrors
// the LibreChat fork's "正在规划答案… (3s)" affordance using the progressStore
// (nova.* events) for the current Chinese step + a live timer. Carries a
// role-bar (to align under the assistant column) but NO agent-name header, so
// it doesn't duplicate the running assistant bubble's header above it. Hides
// once all plan steps are done so the streamed answer text takes over.
function RunningIndicator(): JSX.Element | null {
  const { t } = useTranslation();
  const thread = useThread({ optional: true });
  const progress = useSyncExternalStore(
    subscribeProgress,
    getProgressSnapshot,
    getProgressSnapshot,
  );
  const running = Boolean(thread?.isRunning) || progress.running;

  const [, setTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const h = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(h);
  }, [running]);

  const allStepsDone =
    progress.steps.length > 0 && progress.steps.every((s) => s.status === "done");
  if (!running || allStepsDone) return null;

  const activeStep = progress.steps.find((s) => s.status === "started");
  const label = activeStep
    ? activeStep.description
    : t("chat.activity.phase.planning");
  const elapsed = progress.runStartedAt
    ? `${Math.round((Date.now() - progress.runStartedAt) / 1000)}s`
    : null;

  return (
    <div className="teacher-message from-assistant teacher-run-indicator">
      <div className="role-bar" />
      <div className="body">
        <div className="content">
          <div className="run-row">
            <span className="run-spinner" aria-hidden />
            <span className="run-label">{label}</span>
            {elapsed && <span className="run-time">{elapsed}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
